import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import { getAllDoctors } from 'api/doctors';
import { getAllTreatingOrgs } from 'api/treating_orgs';
import { createTreatment } from 'api/treatments';

import ViewComponent from './view_component/index';

import { TYPES } from 'config/treatment';
import { BLANK } from 'config/shared';

import { serialize } from 'components/text_editor/text_editor_config';

const TreatmentAdd = ({ onMessages, onCountUpdate }) => {
  const [fetching, setFetching] = useState(false);
  const [treatment, setTreatment] = useState({
    type: TYPES.find((t) => t.id === 'cpap-initial'),
  });
  const [treatingOrgOptions, setTreatingOrgOptions] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [hasOtherTreatmentType, setHasOtherTreatmentType] = useState(false);
  const [validation, setValidation] = useState({
    doctor: true,
    type: true,
    referralStatus: true,
    treatingOrg: true,
    customer: true,
    referredAt: true,
  });
  const history = useHistory();

  useEffect(() => {
    (async () => {
      const allDoctors = await getAllDoctors();
      const allTreatingOrgs = await getAllTreatingOrgs();

      const doctorOptions = allDoctors
        .map((d) => ({
          id: d.id,
          value: d.id,
          label: `${d.lastName?.toUpperCase()} ${d.firstName}`,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label));

      const treatingOrgOptions = allTreatingOrgs.map((d) => ({
        id: d.id,
        value: d.id,
        label: d.name,
      }));
      treatingOrgOptions.unshift(BLANK);

      setDoctorOptions([BLANK, ...doctorOptions]);
      setTreatingOrgOptions(treatingOrgOptions);
      setFetching(false);
    })();
  }, []);

  const onSelectChange = (field, selectedOption) => {
    setTreatment({
      ...treatment,
      [field]: selectedOption,
    });

    if (field === 'type' && selectedOption.id === 'other')
      setHasOtherTreatmentType(true);
  };

  const onDateChange = ({ value }) => {
    setTreatment({
      ...treatment,
      referredAt: value,
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;

    setTreatment({
      ...treatment,
      [name]: value,
    });
  };

  const onMachineSettingsEditorChange = (data) => {
    setTreatment({
      ...treatment,
      machineSettings: data,
    });
  };
  const onNotesEditorChange = (data) => {
    setTreatment({
      ...treatment,
      notes: data,
    });
  };

  const checkValidity = useCallback(() => {
    const validation = {
      doctor: !!treatment.doctor,
      type: !!treatment.type,
      referralStatus: true,
      treatingOrg: true,
      customer: !!treatment.customer,
      referredAt: true, // can be blank or with Date
    };
    setValidation(validation);

    return Object.values(validation).every(Boolean);
  }, [treatment]);

  const onSubmit = useCallback(async () => {
    setFetching(true);

    if (checkValidity()) {
      const {
        meta: { messages },
      } = await createTreatment({
        ...treatment,
        type: treatment.type?.value || treatment.type, //respect "other" free text
        treatingOrg: treatment.treatingOrg?.value || treatment.treatingOrg,
        referralStatus: treatment.referralStatus?.value,
        customer: treatment.customer?.id,
        doctor: treatment.doctor?.id,
        machineSettings: Array.isArray(treatment.machineSettings)
          ? treatment?.machineSettings.map(serialize).join('')
          : treatment.machineSettings,
        notes: Array.isArray(treatment.notes)
          ? treatment?.notes.map(serialize).join('')
          : treatment.notes,
      });

      onMessages(messages);
      onCountUpdate();

      history.goBack();
    }

    setFetching(false);
  }, [checkValidity, history, treatment, onMessages, onCountUpdate]);

  const viewProps = {
    onSubmit,
    onSelectChange,
    onDateChange,
    onChange,
    hasOtherTreatmentType,
    treatment,
    doctorOptions,
    treatingOrgOptions,
    validation,
    onMachineSettingsEditorChange,
    onNotesEditorChange,
  };

  return fetching ? (
    <main className="th-main"></main>
  ) : (
    <ViewComponent {...viewProps} />
  );
};

TreatmentAdd.propTypes = {
  onMessages: PropTypes.func.isRequired,
  onCountUpdate: PropTypes.func.isRequired,
};

export default TreatmentAdd;
